// homeChatLaunchBus — pub/sub used by the home page Learn Everything
// feed (and the Roadmap drawer, Hackathon CTA, etc.) to ask the
// PlaybookSearchHero to launch a playbook inside its UnifiedChatShell.
//
// Why a bus and not a context: the hero owns the chat shell as local
// state for performance reasons (we don't want a context provider
// re-rendering the entire home tree on every chat tick). A tiny
// pub/sub keeps the wiring zero-coupling: any component can fire
// `launchOnHome(payload)` and the hero (the single subscriber) picks
// it up, swaps out any active conversation (which is auto-saved to
// `chat_conversations` so it lands in the user's "Past chats"
// sidebar), and starts a fresh chat seeded with the chosen playbook.
import type { Playbook } from "@/data/playbooks";

export type HomeChatLaunchPayload = {
  /** The playbook to load into the chat shell. */
  playbook: Playbook;
  /** Topic / title shown in the seed pill and used for {topic} interpolation. */
  topic: string;
  /** Free-form analytics label, e.g. "feed:quest", "feed:mastery", "drawer:roadmap". */
  source: string;
  /** Optional: jump to a specific step instead of step 0. */
  startStep?: number;
  /** Optional: auto-send the first composer prompt (skip the click-to-send). */
  autoSend?: boolean;
};

type Listener = (p: HomeChatLaunchPayload) => void;

const listeners = new Set<Listener>();

/** Fire a launch. The hero is expected to be mounted; if not (e.g. user
 * is mid-route-change), the payload is dropped — callers should always
 * also be on `/` when they call this. */
export function launchOnHome(payload: HomeChatLaunchPayload): void {
  for (const l of listeners) l(payload);
}

export function subscribeHomeChatLaunch(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}